// ============================================================================
// CENTRALIZED USER TYPES AND ROLE CONSTANTS
// ============================================================================
//
// PURPOSE: Single source of truth for all user types and role-related constants
// 
// FEATURES:
// - Centralized role definitions
// - Type-safe role constants
// - Reusable role options for selects
// - Consistent role display names
// - Role validation utilities
//
// USAGE:
// - Import role constants instead of hardcoding strings
// - Use role options for select components
// - Validate roles with centralized functions
//
// ============================================================================

// ============================================================================
// ROLE CONSTANTS
// ============================================================================

export const USER_ROLES = {
  WTC_ADMIN: "wtc_admin",
  WTC_EMPLOYEE: "wtc_employee", 
  WTC_CRM: "wtc_crm",
  CLIENT_USER: "client_user",
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// ============================================================================
// ROLE DISPLAY NAMES
// ============================================================================

export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]: "Admin",
  [USER_ROLES.WTC_EMPLOYEE]: "Employee", 
  [USER_ROLES.WTC_CRM]: "CRM",
  [USER_ROLES.CLIENT_USER]: "Client",
} as const;

// ============================================================================
// ROLE DESCRIPTIONS
// ============================================================================

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]: "Full system access - can see everything and manage all aspects of the system",
  [USER_ROLES.WTC_EMPLOYEE]: "Full system access - can see everything and manage all aspects of the system",
  [USER_ROLES.WTC_CRM]: "Full system access - can see everything and manage all aspects of the system", 
  [USER_ROLES.CLIENT_USER]: "Full system access - can see everything and manage all aspects of the system",
} as const;

// ============================================================================
// ROLE OPTIONS FOR SELECT COMPONENTS
// ============================================================================

export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  {
    value: USER_ROLES.WTC_ADMIN,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_ADMIN],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_ADMIN],
  },
  {
    value: USER_ROLES.WTC_EMPLOYEE,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_EMPLOYEE],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_EMPLOYEE],
  },
  {
    value: USER_ROLES.WTC_CRM,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_CRM],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_CRM],
  },
  {
    value: USER_ROLES.CLIENT_USER,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.CLIENT_USER],
    description: ROLE_DESCRIPTIONS[USER_ROLES.CLIENT_USER],
  },
];

// ============================================================================
// ROLE VALIDATION UTILITIES
// ============================================================================

/**
 * Check if a string is a valid user role
 */
export function isValidUserRole(role: string): role is UserRole {
  return Object.values(USER_ROLES).includes(role as UserRole);
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole | string): string {
  return ROLE_DISPLAY_NAMES[role as UserRole] || role;
}

/**
 * Get role description
 */
export function getRoleDescription(role: UserRole | string): string {
  return ROLE_DESCRIPTIONS[role as UserRole] || "Unknown role";
}

// ============================================================================
// ROLE GROUPING UTILITIES
// ============================================================================

/**
 * Check if role is an employee role (all roles now have full access)
 */
export function isEmployeeRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  
  return [
    USER_ROLES.WTC_ADMIN,
    USER_ROLES.WTC_EMPLOYEE,
    USER_ROLES.WTC_CRM,
    USER_ROLES.CLIENT_USER,
  ].includes(role as any);
}

/**
 * Check if role is an admin role
 */
export function isAdminRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.WTC_ADMIN;
}

/**
 * Check if role is a CRM role
 */
export function isCRMRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.WTC_CRM;
}

/**
 * Check if role is a client role
 */
export function isClientRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.CLIENT_USER;
}

// ============================================================================
// ROLE PERMISSIONS
// ============================================================================

export interface RolePermissions {
  can_manage_users: boolean;
  can_manage_contacts: boolean;
  can_manage_tickets: boolean;
  can_manage_time_entries: boolean;
  can_access_all_contacts: boolean;
  can_access_all_tickets: boolean;
  can_access_all_time_entries: boolean;
  can_manage_automation: boolean;
  can_manage_templates: boolean;
  can_access_reports: boolean;
  can_manage_quickbooks: boolean;
  can_manage_integrations: boolean;
  can_access_crm_functions: boolean;
  can_manage_proposals: boolean;
  can_manage_meetings: boolean;
  can_manage_tasks: boolean;
  can_view_own_tickets: boolean;
  can_view_own_time_entries: boolean;
  can_view_own_communications: boolean;
}

// Full access permissions for all user types
const FULL_ACCESS_PERMISSIONS: RolePermissions = {
  can_manage_users: true,
  can_manage_contacts: true,
  can_manage_tickets: true,
  can_manage_time_entries: true,
  can_access_all_contacts: true,
  can_access_all_tickets: true,
  can_access_all_time_entries: true,
  can_manage_automation: true,
  can_manage_templates: true,
  can_access_reports: true,
  can_manage_quickbooks: true,
  can_manage_integrations: true,
  can_access_crm_functions: true,
  can_manage_proposals: true,
  can_manage_meetings: true,
  can_manage_tasks: true,
  can_view_own_tickets: true,
  can_view_own_time_entries: true,
  can_view_own_communications: true,
};

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  [USER_ROLES.WTC_ADMIN]: FULL_ACCESS_PERMISSIONS,
  [USER_ROLES.WTC_CRM]: FULL_ACCESS_PERMISSIONS,
  [USER_ROLES.WTC_EMPLOYEE]: FULL_ACCESS_PERMISSIONS,
  [USER_ROLES.CLIENT_USER]: FULL_ACCESS_PERMISSIONS,
};

/**
 * Get permissions for a specific role
 */
export function getRolePermissions(role: UserRole | string): RolePermissions {
  return ROLE_PERMISSIONS[role as UserRole] || ROLE_PERMISSIONS[USER_ROLES.CLIENT_USER];
}

/**
 * Check if a role has a specific permission
 */
export function hasRolePermission(role: UserRole | string, permission: keyof RolePermissions): boolean {
  const permissions = getRolePermissions(role);
  
return permissions[permission] === true;
}
